import { features, useSelector, useThunkDispatch } from '@tunasong/redux'
import type { Agent, Entity, Persisted } from '@tunasong/schemas'
import { useSidebar } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC } from 'react'
import { useCallback } from 'react'
import { AgentRail } from './agent-rail.js'
import { useAgentChildren } from './use-agent-children.js'
import styles from './agent-integration.module.css'

// Re-exports for the `/integration` subpath. The app shell's sidebar
// (`SidebarAgents`) consumes these to render the agent chat tool without
// depending on plugin internals.
//
// Co-locating the re-exports here means the package's `/integration`
// export entry (in `package.json`) doesn't need a separate barrel file
// — the agent plugin's whole "integrate me into a host shell" surface
// lives in this one module. The react-refresh warning that this triggers
// (non-component exports from a component file) is accepted: HMR for
// these hooks is a non-goal and the alternative — a parallel barrel
// pointed at by the package export — is more friction than the win.
/* eslint-disable react-refresh/only-export-components */
export { useAgentChildren } from './use-agent-children.js'
export { ProposalsHeader } from './proposals-header.js'
export { AgentChat } from './agent-chat.js'
export {
  resolveDefaultColor,
  resolveDefaultIconKey,
  resolveDefaultPrompts,
  resolveDefaultRoleLine,
} from './role-defaults.js'
export { resolveAgentIcon, resolveAgentVisuals } from './agent-visuals.js'
export type { AgentVisuals } from './agent-visuals.js'
export { useInFlightAgentStream } from './use-in-flight-agent-stream.js'
export type { InFlightAgentStream } from './use-in-flight-agent-stream.js'
/* eslint-enable react-refresh/only-export-components */

export interface AgentIntegrationProps {
  entity: Persisted<Entity> | null | undefined
  /** Gate the integration behind a feature flag. */
  enabled?: boolean
  className?: string
}

/**
 * Single mount point for the per-entity agent rail in the entity header.
 *
 * Renders the right-gutter rail of agent avatars (with unread dots and
 * runtime-status indicators). Clicking an avatar surfaces the Agents tool
 * in the sidebar with that agent's chat thread active — the panel itself
 * lives inside the sidebar (`SidebarAgents` in
 * `apps/tunasong/tunasong-ui/src/sidebar/sidebar-agents.tsx`).
 *
 * The legacy standalone right-edge Drawer has been removed in favour of
 * the existing sidebar surface; per-agent identity (avatar, unread dot,
 * runtime status) remains in the rail and the user can also enter the
 * panel via the sidebar's Agents icon or the `agent-toggle-panel` global
 * command (default Cmd+Shift+J).
 *
 * The active-agent selection is mirrored with `SidebarAgents` via
 * `ui.sidebar.activeAgentId`, cleared by the reducer whenever the active
 * tool moves off `'Agents'` (so the rail naturally un-highlights when the
 * user switches tools).
 *
 * Per-block proposal cues live on the `ProposalGutterChip` rendered by
 * the agent plugin's `BlockDecoration` slot — not here.
 */
export const AgentIntegration: FC<AgentIntegrationProps> = ({ entity, enabled = true, className }) => {
  const dispatch = useThunkDispatch()
  const { setActiveTool } = useSidebar()
  const agents = useAgentChildren(enabled ? entity?.id : undefined)

  // Rail-highlight selector. Reads the sidebar's active-agent id when the
  // Agents tool is open; returns null otherwise so the rail un-highlights
  // when the user moves to a different tool. Mirrors the reducer's own
  // clear-on-tool-switch behaviour.
  const activeAgentIdForHighlight = useSelector(state =>
    state.ui.sidebar.activeTool === 'Agents' ? state.ui.sidebar.activeAgentId : null
  )

  const handleSelect = useCallback(
    (agent: Persisted<Agent>) => {
      dispatch(features.ui.actions.setSidebarActiveAgentId(agent.id))
      setActiveTool('Agents')
    },
    [dispatch, setActiveTool]
  )

  if (agents.length === 0) {
    return null
  }

  return (
    <AgentRail
      parentId={entity?.id}
      activeAgentId={activeAgentIdForHighlight}
      onSelect={handleSelect}
      // Positioning is owned by the integrator (see the rail's own CSS comment).
      // The 48px-tall `overflow: hidden` header toolbar would clip a vertical
      // column of avatars, so apply fixed positioning regardless of mount.
      className={cn(styles.rail, className)}
    />
  )
}
